import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { EmailInput } from '../forms/EmailInput';
import {
  SuperAdminService,
  CompanyAdminUpdateData,
  UserProfile,
} from '../../services/superAdminService';

interface EditCompanyAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  adminId: string;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
}

interface ValidationErrors {
  name?: string;
  email?: string;
  phone?: string;
}

const NAME_MAX_LENGTH = 50;

const EditCompanyAdminModal: React.FC<EditCompanyAdminModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  adminId,
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
  });

  const [originalData, setOriginalData] = useState<UserProfile | null>(null);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);

  const superAdminService = React.useMemo(() => new SuperAdminService(), []);

  // 管理者データ取得
  const loadAdminData = React.useCallback(async () => {
    if (!adminId || !isOpen) return;

    setIsLoadingData(true);
    try {
      const data = await superAdminService.getCompanyAdminById(adminId);
      setOriginalData(data);
      setFormData({
        name: data.name,
        email: data.email,
        phone: data.phone ?? '', // Use phone from data if available
      });
      setIsEmailValid(true);
    } catch (error) {
      logger.error('Failed to load admin data:', error);
      setSubmitError('管理者情報の取得に失敗しました');
    } finally {
      setIsLoadingData(false);
    }
  }, [adminId, isOpen, superAdminService]);

  useEffect(() => {
    if (isOpen && adminId) {
      loadAdminData();
      setErrors({});
      setSubmitError('');
      setShowConfirmation(false);
    }
  }, [isOpen, adminId, loadAdminData]);

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // リアルタイムバリデーション
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  // メールアドレスバリデーション結果の処理
  const handleEmailValidationChange = (valid: boolean) => {
    setIsEmailValid(valid);
  };

  // メールアドレス変更ハンドラー
  const handleEmailChange = (_name: string, value: string) => {
    handleInputChange('email', value);
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // 名前
    if (!formData.name.trim()) {
      newErrors.name = '名前を入力してください';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = '名前は2文字以上で入力してください';
    } else if (formData.name.trim().length > NAME_MAX_LENGTH) {
      newErrors.name = `名前は${NAME_MAX_LENGTH}文字以内で入力してください`;
    }

    // メールアドレス（統一バリデーションを使用）
    if (!isEmailValid) {
      newErrors.email = 'メールアドレスを入力してください';
    }

    // 電話番号（任意）
    if (formData.phone && !/^[\d-+().\s]+$/.test(formData.phone)) {
      newErrors.phone = '正しい電話番号形式で入力してください';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    if (!showConfirmation) {
      setShowConfirmation(true);
      return;
    }

    setIsLoading(true);
    setSubmitError('');

    try {
      const updateData: CompanyAdminUpdateData = {};

      // 変更があったフィールドのみ更新
      if (formData.name !== originalData?.name) {
        updateData.name = formData.name.trim();
      }
      if (formData.email !== originalData?.email) {
        updateData.email = formData.email;
      }
      if (formData.phone && formData.phone.trim()) {
        updateData.phone = formData.phone.trim();
      }

      if (Object.keys(updateData).length === 0) {
        setSubmitError('変更がありません');
        setShowConfirmation(false);
        setIsLoading(false);
        return;
      }

      await superAdminService.updateCompanyAdmin(adminId, updateData);
      onSuccess();
      onClose();
    } catch (error: any) {
      logger.error('Failed to update company admin:', error);
      setSubmitError(error.message || '管理者情報の更新に失敗しました');
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title='管理者情報を編集' size='lg'>
      <div className='space-y-6'>
        {submitError && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4'>
            <div className='flex'>
              <ExclamationTriangleIcon className='h-5 w-5 text-red-400 dark:text-red-300' />
              <div className='ml-3'>
                <p className='text-sm text-red-800 dark:text-red-200'>{submitError}</p>
              </div>
            </div>
          </div>
        )}

        {showConfirmation && (
          <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4'>
            <div className='flex'>
              <CheckCircleIcon className='h-5 w-5 text-blue-400 dark:text-blue-300' />
              <div className='ml-3'>
                <h3 className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                  更新内容の確認
                </h3>
                <div className='mt-2 text-sm text-blue-700 dark:text-blue-300 space-y-1'>
                  {formData.name !== originalData?.name && (
                    <p>
                      <strong>管理者名:</strong> {originalData?.name} → {formData.name}
                    </p>
                  )}
                  {formData.email !== originalData?.email && (
                    <p>
                      <strong>メールアドレス:</strong> {originalData?.email} → {formData.email}
                    </p>
                  )}
                  {formData.phone && (
                    <p>
                      <strong>電話番号:</strong> {formData.phone}
                    </p>
                  )}
                </div>
                <p className='mt-2 text-sm text-blue-600 dark:text-blue-300'>
                  この内容で更新しますか？
                </p>
              </div>
            </div>
          </div>
        )}

        {isLoadingData ? (
          <div className='text-center py-12'>
            <div className='inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600'></div>
            <p className='mt-2 text-sm text-gray-500 dark:text-gray-400'>読み込み中...</p>
          </div>
        ) : (
          <div className='grid grid-cols-1 gap-6'>
            {/* 会社名（読み取り専用） */}
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>
                会社名 <span className='text-gray-500 dark:text-gray-400'>(変更不可)</span>
              </label>
              <Input
                type='text'
                value={originalData?.company_name || ''}
                disabled
                className='bg-gray-100 dark:bg-gray-700'
              />
              <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                会社の変更はできません。必要な場合は削除後に再作成してください。
              </p>
            </div>

            {/* 管理者名 */}
            <div>
              <label
                htmlFor='name'
                className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
              >
                管理者名 <span className='text-red-500'>*</span>
              </label>
              <Input
                id='name'
                type='text'
                value={formData.name}
                onChange={e => handleInputChange('name', e.target.value)}
                placeholder='山田太郎'
                error={errors.name}
                maxLength={NAME_MAX_LENGTH}
              />
              <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                {formData.name.length}/{NAME_MAX_LENGTH}文字
              </p>
            </div>

            {/* メールアドレス */}
            <EmailInput
              name='email'
              label='メールアドレス'
              value={formData.email}
              onChange={handleEmailChange}
              onValidationChange={handleEmailValidationChange}
              placeholder='admin@example.com'
              required={true}
              disabled={isLoading}
              validationOptions={{ required: true }}
              error={errors.email}
            />

            {/* 電話番号（任意） */}
            <div>
              <label
                htmlFor='phone'
                className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
              >
                電話番号 <span className='text-gray-500 dark:text-gray-400'>(任意)</span>
              </label>
              <Input
                id='phone'
                type='tel'
                value={formData.phone}
                onChange={e => handleInputChange('phone', e.target.value)}
                placeholder='03-1234-5678'
                error={errors.phone}
              />
            </div>
          </div>
        )}

        {/* ボタン */}
        <div className='flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700'>
          <Button
            variant='secondary'
            onClick={() => {
              if (showConfirmation) {
                setShowConfirmation(false);
              } else {
                onClose();
              }
            }}
            disabled={isLoading || isLoadingData}
          >
            {showConfirmation ? '戻る' : 'キャンセル'}
          </Button>
          <Button
            variant='primary'
            onClick={handleSubmit}
            disabled={isLoading || isLoadingData}
            loading={isLoading}
          >
            {showConfirmation ? '更新する' : '確認画面へ'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EditCompanyAdminModal;
